/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */

import React from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

// Custom bar shape factory - creates a shape component that knows the metric order and stackIds
const createRoundedStackBar = (displayMetrics: string[], stackIds: Record<string, string>) => {
  return (props: any) => {
    const { fill, x, y, width, height, payload, dataKey } = props;

    // If no height, return empty element
    if (!height || height <= 0) return <g />;

    // Get the stackId for the current bar
    const currentStackId = stackIds[dataKey] || "stack";

    // Check if this is a negative bar
    const isNegative = payload[dataKey] < 0;

    let shouldRound = false;

    // Only apply rounding to positive bars
    if (!isNegative) {
      // For positive bars, find the topmost visible bar
      let topmostMetric = null;
      for (let i = displayMetrics.length - 1; i >= 0; i--) {
        const metric = displayMetrics[i];
        const metricStackId = stackIds[metric] || "stack";
        if (metricStackId === currentStackId && payload[metric] > 0) {
          topmostMetric = metric;
          break;
        }
      }
      shouldRound = topmostMetric === dataKey;
    }

    const radius = shouldRound ? 8 : 0;

    // Draw rectangle with optional rounded corners (only for positive bars)
    if (radius > 0 && !isNegative) {
      // For positive bars, round the top corners
      return (
        <path
          d={`
            M ${x},${y + radius}
            Q ${x},${y} ${x + radius},${y}
            L ${x + width - radius},${y}
            Q ${x + width},${y} ${x + width},${y + radius}
            L ${x + width},${y + height}
            L ${x},${y + height}
            Z
          `}
          fill={fill}
        />
      );
    }

    // Regular rectangle for non-topmost bars
    return <rect x={x} y={y} width={width} height={height} fill={fill} />;
  };
};

// Base timeseries data structure
export interface TimeSeriesDataPoint {
  date: string; // ISO date for sorting/filtering
  dateLabel: string; // Formatted label for display
  periodStart?: Date; // Optional raw date for internal use
  [metric: string]: number | string | Date | undefined; // Dynamic metric values
}

// Extract just the metric keys (excludes date/dateLabel/periodStart)
export type MetricKey<T extends TimeSeriesDataPoint> = Exclude<keyof T, "date" | "dateLabel" | "periodStart">;

// Type-safe chart configuration
export interface ChartConfig<T extends TimeSeriesDataPoint> {
  // List of metrics to display (must be keys from T)
  metrics: MetricKey<T>[];

  // Only allow formatters for actual metrics in the data
  formatters: Partial<Record<MetricKey<T>, (value: number) => string>>;

  // Only allow colors for actual metrics in the data
  colors: Partial<Record<MetricKey<T>, string>>;

  // Only allow labels for actual metrics in the data
  labels: Partial<Record<MetricKey<T>, string>>;

  // Optional: Custom stack IDs per metric (for stacked variant with mixed positive/negative values)
  stackIds?: Partial<Record<MetricKey<T>, string>>;

  // Optional: Display order for legend and tooltip (if different from metrics array)
  displayOrder?: MetricKey<T>[];
}

interface MetricsBarChartViewProps<T extends TimeSeriesDataPoint> {
  data: T[];
  config: ChartConfig<T>;

  // Visualization variant
  variant?: "stacked" | "sideBySide";

  // Chart dimensions & styling
  height?: string | number;
  showLegend?: boolean;

  // Y-axis configuration
  yAxisConfig?: {
    width?: number;
    left?: { width?: number };
    right?: { width?: number }; // Only for sideBySide
  };

  // Bar gap configuration (for overlapping bars with different stackIds)
  barGap?: number | string;
}

export const MetricsBarChartView = <T extends TimeSeriesDataPoint>({
  data,
  config,
  variant = "sideBySide",
  height,
  showLegend = true,
  yAxisConfig,
  barGap,
}: MetricsBarChartViewProps<T>) => {
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  // Early return if no data
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Early return if no metrics configured
  if (!config.metrics || config.metrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No metrics selected</p>
      </div>
    );
  }

  // Get formatters with defaults
  const getFormatter = (metric: MetricKey<T>) => {
    return config.formatters[metric] || ((v: number) => v.toString());
  };

  const getColor = (metric: MetricKey<T>) => {
    return config.colors[metric] || "#6b7280";
  };

  const getLabel = (metric: MetricKey<T>) => {
    return config.labels[metric] || (metric as string);
  };

  const getStackId = (metric: MetricKey<T>) => {
    return config.stackIds?.[metric] || "stack";
  };

  // For sideBySide mode, only use first 2 metrics (dual Y-axes)
  const displayMetrics = variant === "sideBySide" ? config.metrics.slice(0, 2) : config.metrics;
  const primaryMetric = displayMetrics[0];
  const secondaryMetric = displayMetrics[1];

  // Use displayOrder for legend/tooltip if provided, otherwise use displayMetrics
  const legendOrder = config.displayOrder || displayMetrics;

  // Custom tooltip content that respects displayOrder
  const CustomTooltip = ({ active, payload, label, coordinate, viewBox }: any) => {
    if (!active || !payload) return null;

    // Create a map of metric -> value from payload
    const dataMap = new Map(payload.map((entry: any) => [entry.dataKey, entry]));

    // Render in displayOrder
    const orderedEntries = legendOrder
      .map((metric) => dataMap.get(metric as string))
      .filter((entry) => entry !== undefined);

    // Calculate if tooltip would overflow viewport
    const tooltipWidth = 250;
    const viewportWidth = window.innerWidth;

    // Get the chart's position on screen using the ref
    let absoluteX = coordinate?.x || 0;
    if (chartContainerRef.current) {
      const chartRect = chartContainerRef.current.getBoundingClientRect();
      absoluteX = chartRect.left + (coordinate?.x || 0);
    }

    const wouldOverflowRight = absoluteX + tooltipWidth + 10 > viewportWidth - 20;

    // Calculate spacing from chart data
    const chartWidth = viewBox?.width || 0;
    const dataLength = data?.length || 1;
    // Total available width per data point (includes bar + gaps)
    const widthPerPoint = chartWidth / dataLength;
    const offsetFromCenter = widthPerPoint / 2 + 10;

    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          padding: "8px 12px",
          zIndex: 1000,
          transform: wouldOverflowRight ? `translateX(calc(-100% - ${offsetFromCenter}px))` : undefined,
          pointerEvents: "none",
        }}
      >
        <p style={{ margin: "0 0 4px 0", fontWeight: 600 }}>{label}</p>
        {orderedEntries.map((entry: any) => {
          const metric = entry.dataKey as MetricKey<T>;
          const formatter = getFormatter(metric);
          const metricLabel = getLabel(metric);
          return (
            <div key={entry.dataKey} style={{ display: "flex", alignItems: "center", gap: "8px", margin: "4px 0" }}>
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: entry.fill }}></div>
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                {metricLabel}: <strong>{formatter(entry.value)}</strong>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Dynamic X-axis configuration based on data density
  const shouldRotateLabels = data.length > 10;
  const xAxisConfig = shouldRotateLabels
    ? { angle: -45, textAnchor: "end" as const, height: 60 }
    : { angle: 0, textAnchor: "middle" as const, height: 30 };
  const chartBottomMargin = shouldRotateLabels ? 60 : 20;

  return (
    <div className="h-full flex flex-col min-h-0" style={{ transition: "none", height }}>
      {/* Custom Legend - positioned at top left for all variants when showLegend is true */}
      {showLegend && (
        <div className="flex items-center gap-6 mb-4">
          {legendOrder.map((metric) => (
            <div key={metric as string} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getColor(metric) }}></div>
              <span className="text-base font-medium text-gray-600">{getLabel(metric)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bar Chart */}
      <div className="flex-1 min-h-0 transition-none" ref={chartContainerRef}>
        <ResponsiveContainer width="100%" height="100%" debounce={0}>
          <ComposedChart
            data={data}
            margin={{ top: 20, right: 20, left: 20, bottom: chartBottomMargin }}
            {...(barGap !== undefined && { barGap })}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
              angle={xAxisConfig.angle}
              textAnchor={xAxisConfig.textAnchor}
              height={xAxisConfig.height}
            />

            {variant === "stacked" ? (
              // Stacked mode: single Y-axis
              <YAxis
                tick={{ fontSize: 12, fill: "#6b7280" }}
                tickLine={false}
                axisLine={false}
                width={yAxisConfig?.width || 70}
                tickFormatter={(value) => getFormatter(primaryMetric)(value)}
                domain={["dataMin", "dataMax"]}
              />
            ) : (
              // Side-by-side mode: dual Y-axes
              <>
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  tickLine={false}
                  axisLine={false}
                  width={yAxisConfig?.left?.width || 70}
                  tickFormatter={(value) => getFormatter(primaryMetric)(value)}
                />
                {secondaryMetric && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={false}
                    width={yAxisConfig?.right?.width || 70}
                    tickFormatter={(value: any) => getFormatter(secondaryMetric)(value)}
                  />
                )}
              </>
            )}

            <Tooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 1000 }}
              allowEscapeViewBox={{ x: true, y: true }}
            />

            {variant === "stacked"
              ? // Stacked bars - use custom shape to apply radius only to topmost visible bar
                (() => {
                  // Build stackIds map for the custom shape
                  const stackIdsMap: Record<string, string> = {};
                  displayMetrics.forEach((metric) => {
                    stackIdsMap[metric as string] = getStackId(metric);
                  });

                  // Create the custom shape with knowledge of metric order and stack groups
                  const RoundedStackBar = createRoundedStackBar(
                    displayMetrics.map((m) => m as string),
                    stackIdsMap,
                  );

                  return displayMetrics.map((metric) => {
                    const stackId = getStackId(metric);
                    // Use custom shape only for positive bars, regular bars for negative
                    if (stackId === "negative") {
                      return (
                        <Bar
                          key={metric as string}
                          dataKey={metric as string}
                          stackId={stackId}
                          fill={getColor(metric)}
                          isAnimationActive={false}
                        />
                      );
                    }
                    return (
                      <Bar
                        key={metric as string}
                        dataKey={metric as string}
                        stackId={stackId}
                        fill={getColor(metric)}
                        isAnimationActive={false}
                        shape={RoundedStackBar}
                      />
                    );
                  });
                })()
              : // Side-by-side bars - separate Y-axes
                displayMetrics.map((metric, index) => (
                  <Bar
                    key={metric as string}
                    yAxisId={index === 0 ? "left" : "right"}
                    dataKey={metric as string}
                    fill={getColor(metric)}
                    fillOpacity={0.8}
                    isAnimationActive={false}
                    radius={[8, 8, 0, 0]}
                  />
                ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
